import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { RequestParams, ApiResponse } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import { TASK_KEYS } from '../lib';
import { TaskApi } from '../api';
import type { TaskBase, TaskCreatePayload, TaskUpdatePayload } from './types';

export const useTaskList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<TaskBase>>({
    queryKey: TASK_KEYS.list(params),
    queryFn: () => TaskApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

export const useTaskById = (id: number) => {
  return useQuery<ApiResponse<TaskBase>>({
    queryKey: TASK_KEYS.detail(id),
    queryFn: () => TaskApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TaskCreatePayload) => TaskApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.all });
    },
  });
};

export const useUpdateTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TaskUpdatePayload) => TaskApi.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.all });
    },
  });
};

export const useDeleteTask = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => TaskApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TASK_KEYS.all });
    },
  });
};
